import { Component, OnInit, AfterViewInit, ChangeDetectorRef } from '@angular/core';
import { trigger, transition, style, animate } from '@angular/animations';
import { DestructorComponent } from 'src/app/components/destructor/destructor.component';
import { PaginatedResponse, PaginationInfo } from 'src/app/shared/models';
import { AuctionTransportFeeService } from 'src/app/services/auction-transport/auction-transport-fee.service';
import { AuctionTransportFeeModalService } from 'src/app/services/auction-transport-fee-modal/auction-transport-fee-modal.service';
import Swal from 'sweetalert2';

declare var bootstrap: any;

@Component({
  selector: 'app-cal-auction-transport-fee',
  templateUrl: './cal-auction-transport-fee.component.html',
  styleUrls: ['./cal-auction-transport-fee.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition(':enter', [
        style({ height: '0px', opacity: 0 }),
        animate('300ms ease-in', style({ height: '*', opacity: 1 }))
      ]),
      transition(':leave', [
        animate('300ms ease-out', style({ height: '0px', opacity: 0 }))
      ])
    ])
  ]
})
export class CalAuctionTransportFeeComponent extends DestructorComponent implements OnInit, AfterViewInit {

  // Data properties
  auctionTransportFees: any[] = [];
  isLoading = false;

  // Pagination
  currentPage = 1;
  paginationInfo: PaginationInfo = {
    currentPage: 1,
    perPage: 10,
    total: 0,
    lastPage: 1,
    firstPage: 1
  };

  // Search and filters
  searchTerm = '';
  showFiltersForm = false;
  filters: Record<string, any> = {};

  // Bulk selection
  selectedAuctionTransportFees: string[] = [];

  constructor(
    private _auctionTransportFeeService: AuctionTransportFeeService,
    private _auctionTransportFeeModalService: AuctionTransportFeeModalService,
    private cdr: ChangeDetectorRef
  ) {
    super();
  }

  ngOnInit(): void {
    this.loadAuctionTransportFees();
  }

  ngAfterViewInit(): void {
    // No longer needed since we removed dropdown menus
  }

  loadAuctionTransportFees(page: number = 1, search: string = '', filters: Record<string, any> = {}): void {
    this.isLoading = true;
    this.currentPage = page;
    this.selectedAuctionTransportFees = []; // Clear selections when loading new data
    this.searchTerm = search;
    this.filters = filters;

    this.subscriptions.push(
      this._auctionTransportFeeService.GetData(page.toString(), search, filters).subscribe({
        next: (response: PaginatedResponse<any>) => {
          if (response.status && response.data) {
            this.auctionTransportFees = response.data.data || [];
            this.paginationInfo = response.data.pagination;
            this.currentPage = response.data.pagination.currentPage;
          } else {
            this.auctionTransportFees = [];
            this.resetPagination();
          }
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        },
        error: (error) => {
          console.error('Error loading auction transport fees:', error);
          this.auctionTransportFees = [];
          this.resetPagination();
          this.isLoading = false;
          this.cdr.detectChanges();
          this.toggleToolbars(); // Reset toolbar state
        }
      })
    );
  }

  onPageChange(page: number): void {
    this.loadAuctionTransportFees(page, this.searchTerm, this.filters);
  }

  toggleFilters(): void {
    this.showFiltersForm = !this.showFiltersForm;
  }

  applyFilters(): void {
    this.loadAuctionTransportFees(1, this.searchTerm, this.filters);
  }

  resetFilters(): void {
    this.searchTerm = '';
    this.filters = {};
    this.showFiltersForm = false;
    this.loadAuctionTransportFees();
  }

  openAddAuctionTransportFeeModal(): void {
    this._auctionTransportFeeModalService.clearSelectedAuctionTransportFee();
    const modalElement = document.getElementById('kt_modal_auction_transport_fee_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  openEditModal(auctionTransportFee: any): void {
    this._auctionTransportFeeModalService.setSelectedAuctionTransportFee(auctionTransportFee);
    const modalElement = document.getElementById('kt_modal_auction_transport_fee_add_edit');
    if (modalElement) {
      const modal = new bootstrap.Modal(modalElement);
      modal.show();
    }
  }

  deleteAuctionTransportFee(auctionTransportFee: any): void {
    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete "${auctionTransportFee.auction_hall?.name || 'this auction transport fee'}". This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then((result) => {
      if (result.isConfirmed) {
        this.subscriptions.push(
          this._auctionTransportFeeService.DeleteDataById(auctionTransportFee._id).subscribe({
            next: (response) => {
              if (response.status) {
                Swal.fire('Deleted!', 'Auction transport fee has been deleted.', 'success');
                this.loadAuctionTransportFees(this.currentPage, this.searchTerm, this.filters);
              } else {
                Swal.fire('Error!', response.message || 'Failed to delete auction transport fee.', 'error');
              }
            },
            error: (error) => {
              console.error('Error deleting auction transport fee:', error);
              Swal.fire('Error!', 'An error occurred while deleting the auction transport fee.', 'error');
            }
          })
        );
      }
    });
  }

  deleteSelectedAuctionTransportFees(): void {
    if (this.selectedAuctionTransportFees.length === 0) return;

    Swal.fire({
      title: 'Are you sure?',
      text: `You are about to delete ${this.selectedAuctionTransportFees.length} auction transport fee(s). This action cannot be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete them!'
    }).then((result) => {
      if (result.isConfirmed) {
        let deletedCount = 0;
        const totalCount = this.selectedAuctionTransportFees.length;

        this.selectedAuctionTransportFees.forEach(id => {
          this.subscriptions.push(
            this._auctionTransportFeeService.DeleteDataById(id).subscribe({
              next: () => {
                deletedCount++;
                if (deletedCount === totalCount) {
                  Swal.fire('Deleted!', 'Selected auction transport fees have been deleted.', 'success');
                  this.loadAuctionTransportFees(this.currentPage, this.searchTerm, this.filters);
                }
              },
              error: (error) => {
                console.error('Error deleting auction transport fee:', error);
                deletedCount++;
                if (deletedCount === totalCount) {
                  Swal.fire('Error!', 'Some auction transport fees could not be deleted.', 'error');
                  this.loadAuctionTransportFees(this.currentPage, this.searchTerm, this.filters);
                }
              }
            })
          );
        });
      }
    });
  }

  // Event handlers for modal events
  onAuctionTransportFeeAdded(): void {
    console.log('Auction transport fee added, refreshing list...');
    this.loadAuctionTransportFees(this.currentPage, this.searchTerm, this.filters);
  }

  onAuctionTransportFeeUpdated(): void {
    console.log('Auction transport fee updated, refreshing list...');
    this.loadAuctionTransportFees(this.currentPage, this.searchTerm, this.filters);
  }

  // Utility methods
  private resetPagination(): void {
    this.paginationInfo = {
      currentPage: 1,
      perPage: 10,
      total: 0,
      lastPage: 1,
      firstPage: 1
    };
  }

  // Track by function for ngFor performance
  trackByFn(index: number, item: any): any {
    return item._id || index;
  }

  // Bulk selection methods
  isSelected(auctionTransportFeeId: string): boolean {
    return this.selectedAuctionTransportFees.includes(auctionTransportFeeId);
  }

  toggleSelection(auctionTransportFeeId: string, event: any): void {
    if (event.target.checked) {
      this.selectedAuctionTransportFees.push(auctionTransportFeeId);
    } else {
      const index = this.selectedAuctionTransportFees.indexOf(auctionTransportFeeId);
      if (index > -1) {
        this.selectedAuctionTransportFees.splice(index, 1);
      }
    }
    this.toggleToolbars();
  }

  toggleAllSelection(event: any): void {
    if (event.target.checked) {
      this.selectedAuctionTransportFees = this.auctionTransportFees.map(item => item._id);
    } else {
      this.selectedAuctionTransportFees = [];
    }
    this.toggleToolbars();
  }

  isAllSelected(): boolean {
    return this.auctionTransportFees.length > 0 && this.selectedAuctionTransportFees.length === this.auctionTransportFees.length;
  }

  isIndeterminate(): boolean {
    return this.selectedAuctionTransportFees.length > 0 && this.selectedAuctionTransportFees.length < this.auctionTransportFees.length;
  }

  private toggleToolbars(): void {
    const baseToolbar = document.querySelector('[data-kt-auction-transport-fee-table-toolbar="base"]');
    const selectedToolbar = document.querySelector('[data-kt-auction-transport-fee-table-toolbar="selected"]');

    if (this.selectedAuctionTransportFees.length > 0) {
      baseToolbar?.classList.add('d-none');
      selectedToolbar?.classList.remove('d-none');
    } else {
      baseToolbar?.classList.remove('d-none');
      selectedToolbar?.classList.add('d-none');
    }
  }
}
